<script setup lang="ts">
import { computed, ref } from "vue";
import { PopoverArrow, PopoverContent, PopoverRoot, PopoverTrigger } from "reka-ui";

import { FREE_SEARCH_FIELDS_NAME, FREE_SEARCH_MODE_NAME, FREE_SEARCH_TEXT_NAME } from "@/models/CaseSearchResult";
import CaseSearchFieldDescription from "./CaseSearchFieldDescription.vue";

import type { Column } from "@/models/Column";

const props = defineProps<{
  columns: Column[];
  helpText?: string;
  lastSearch?: Partial<{ searchFields: string[]; searchMode: string; searchText: string }>;
}>();

const helpTextId = `${FREE_SEARCH_TEXT_NAME}-help`;

const value = ref({
  searchText: props.lastSearch?.searchText ?? "",
  searchMode: props.lastSearch?.searchMode ?? "all",
  searchFields: props.lastSearch?.searchFields ?? props.columns.map((col) => col.fieldPath),
});

function massSelect(all: boolean) {
  value.value.searchFields = all ? props.columns.map((col) => col.fieldPath) : [];
}

const sortedColumns = computed(() => {
  return [...props.columns].sort((a, b) => a.displayName.localeCompare(b.displayName));
});
</script>

<template>
  <div class="max-w-md tw-relative tw-shrink-0">
    <div class="tw-flex tw-w-full tw-min-w-0">
      <div class="tw-flex tw-grow tw-flex-col tw-space-y-1">
        <label
          tabindex="0"
          :for="FREE_SEARCH_TEXT_NAME"
          class="tw-text-sm tw-font-medium tw-underline-offset-2 tw-outline-none focus:tw-underline"
        >
          Search across fields
        </label>
        <CaseSearchFieldDescription v-if="helpText" :id="helpTextId" :help-text="helpText" />
        <input
          :id="FREE_SEARCH_TEXT_NAME"
          v-model="value.searchText"
          type="text"
          aria-label="Search across fields"
          :aria-describedby="helpText ? helpTextId : undefined"
          class="tw-rounded-md tw-rounded-r-none tw-border tw-border-r-0 tw-border-gray-500 tw-bg-white tw-p-2"
          :name="FREE_SEARCH_TEXT_NAME"
        />
      </div>
      <div class="tw-flex tw-flex-col tw-space-y-1">
        <label
          tabindex="0"
          :for="FREE_SEARCH_MODE_NAME"
          class="tw-text-sm tw-font-medium tw-underline-offset-2 tw-outline-none focus:tw-underline"
        >
          Type
        </label>
        <select
          :id="FREE_SEARCH_MODE_NAME"
          v-model="value.searchMode"
          aria-label="Criteria to match the search across fields"
          class="tw-min-w-24 tw-rounded-md tw-rounded-l-none tw-border tw-border-gray-500 tw-bg-white tw-p-2"
          :name="FREE_SEARCH_MODE_NAME"
        >
          <option value="all">All words</option>
          <option value="any">Any words</option>
        </select>
      </div>
    </div>
    <PopoverRoot>
      <div class="tw-flex tw-items-center tw-justify-start tw-py-1.5">
        <div class="tw-flex-1"></div>
        <PopoverTrigger
          title="Select fields to apply search to"
          aria-label="Select fields to apply search to"
          class="tw-btn-md-gray tw-group tw-min-w-20"
        >
          <span class="group-data-[state=closed]:tw-hidden">Hide fields selection</span>
          <span class="group-data-[state=open]:tw-hidden">Select fields</span>
        </PopoverTrigger>
      </div>
      <PopoverContent
        side="bottom"
        align="end"
        :align-offset="3"
        :side-offset="3"
        class="tw-b-r tw-z-[100] tw-flex tw-max-h-[calc(var(--reka-popover-content-available-height)-1rem)] tw-max-w-xs tw-flex-col tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-p-2 tw-shadow"
      >
        <div class="tw-col-span-2 tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
          <input
            id="freeText_selectAll"
            type="checkbox"
            :checked="value.searchFields?.length == sortedColumns.length"
            @change="massSelect(($event.target as HTMLInputElement).checked)"
          />
          <label class="tw-text-xs" for="freeText_selectAll">Select All</label>
        </div>
        <ul aria-label="List of fields" class="tw-grid tw-max-w-sm tw-grid-cols-2 tw-gap-x-2 tw-gap-y-1 tw-py-1">
          <li
            v-for="column in sortedColumns"
            :key="column.fieldPath"
            class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2"
          >
            <input
              :id="`${FREE_SEARCH_FIELDS_NAME}_${column.fieldPath}`"
              v-model="value.searchFields"
              type="checkbox"
              :value="column.fieldPath"
            />
            <label class="tw-text-xs" :for="`${FREE_SEARCH_FIELDS_NAME}_${column.fieldPath}`">
              {{ column.displayName }}
            </label>
          </li>
        </ul>
        <PopoverArrow class="tw-fill-white" />
      </PopoverContent>
    </PopoverRoot>
    <input type="hidden" :name="FREE_SEARCH_FIELDS_NAME" :value="JSON.stringify(value.searchFields)" />
  </div>
</template>
